// /onboarding/you — «ما اسمك؟», the PERSON's name (not the business's). A clone
// of the business-name question: the same Wizard column, the same BigInput, the
// counter, the progress line, the «متابعة» button and the error slot. It sits
// inside the account step (٣/٦) right after the PIN, owners only — a member's
// name comes with the invite. The answer is written at once to ihjizli-profile
// (PUT /v1/business/me/identity, person-level: one name per business identity,
// however many businesses), so the team roster and the owner's staff row can
// say who the owner is. A server refusal (422 full_name) lands in the input's
// own error slot; anything else is the shell's error card with a retry.
//
// Two ways in: the wizard (nextAfterAccount: the identity has no name yet →
// here → the business name) and the owner gate (app/OwnerNameGate: an owner
// made before this step existed → here once, `state.then` = where they were
// going → back there).

import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sessionStore } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { BigInput } from '../components/widgets';
import { Wizard, type WizardError } from '../components/Wizard';
import { BUSINESS, OWNER_NAME } from '../copy';
import { failureOf, fetchIdentityName, IDENTITY_KEY, identityFieldError, OWNER_NAME_MAX, saveIdentityName } from '../data/api';
import { ONB } from '../data/flow';
import { onboardingStore, useOnboarding } from '../state';
import type { PathOptions } from '../steps';
import { ContinueAction, useBusinessPanel } from './Business';

/** This question belongs to the owner's path whatever the draft's intent says (the gate sends owners made earlier). */
const OWNER_PATH: Partial<PathOptions> = { intent: 'owner' };

/** Where the owner gate asked us to go back to (an app path only); null = on to the business name. */
export function returnPathOf(state: unknown): string | null {
  const then = (state as { then?: unknown } | null | undefined)?.then;
  return typeof then === 'string' && then.startsWith('/') && !then.startsWith('//') && !then.startsWith('/onboarding') ? then : null;
}

type SysError = { kind: 'network' | 'server'; ref: string | null; detail: string | null };

export function OwnerNameScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const mobile = useLayout() === 'phone';
  const { intent, ownerName } = useOnboarding();
  const authed = sessionStore.isAuthenticated();
  const then = returnPathOf(location.state);
  const identity = useQuery({ queryKey: IDENTITY_KEY, queryFn: fetchIdentityName, enabled: authed, retry: 0, staleTime: 30_000 });
  const panel = useBusinessPanel('name');
  // null = untouched: show what the server has (the saved answer when it could not be read).
  const [typed, setTyped] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sysError, setSysError] = useState<SysError | null>(null);
  const [saving, setSaving] = useState(false);

  if (!authed) return <Navigate to={ONB.phone} replace />;
  if (intent === 'member' && !then) return <Navigate to={ONB.join} replace />;

  const value = typed ?? (identity.isSuccess ? identity.data : ownerName);

  const onChange = (v: string) => {
    setTyped(v);
    setError(null);
    setSysError(null);
  };

  const submit = async () => {
    if (saving) return;
    const name = value.trim();
    if (!name) {
      setError(OWNER_NAME.required);
      return;
    }
    if (Array.from(name).length > OWNER_NAME_MAX) {
      setError(OWNER_NAME.tooLong);
      return;
    }
    setError(null);
    setSysError(null);
    setSaving(true);
    try {
      const saved = await saveIdentityName(name);
      onboardingStore.set({ ownerName: saved });
      qc.setQueryData(IDENTITY_KEY, saved);
      if (then) navigate(then, { replace: true });
      else navigate(ONB.name);
    } catch (e) {
      const field = identityFieldError(e);
      if (field) setError(field);
      else setSysError(failureOf(e));
    } finally {
      setSaving(false);
    }
  };

  const wizardError: WizardError | null = sysError ? { ...sysError, onRetry: () => void submit() } : null;

  return (
    <Wizard
      screen="you"
      testId="onb-you"
      state={saving ? 'saving' : error ? 'validation' : 'default'}
      question={OWNER_NAME.title}
      sub={OWNER_NAME.sub}
      primary={<ContinueAction mobile={mobile} loading={saving} />}
      enterHint
      peek={BUSINESS.nameTitle}
      onSubmit={() => void submit()}
      error={wizardError}
      panel={panel}
      path={OWNER_PATH}
    >
      <BigInput autoFocus data-testid="onb-you-name" maxLength={OWNER_NAME_MAX} placeholder={OWNER_NAME.placeholder} value={value} onChange={onChange} error={error} autoComplete="name" />
    </Wizard>
  );
}
